"use client"
import { useEffect, useState } from "react"
import posthog from "posthog-js"
import { Bot, Check, X } from "lucide-react"
import Dialog from "@/base-components/Dialog"

export type AgentPromptLabels = {
  agent_button: string
  agent_button_title: string
  agent_copied: string
  agent_dialog_title: string
  agent_dialog_close_aria: string
  agent_prompt: string
}

// Copies the one-line prompt that points the user's AI agent at /agents.md,
// which says how to add our MCP server and walk them through setup. Where the
// clipboard is blocked, the text is shown to copy by hand.
const CopyAgentPromptButton = ({
  labels,
  location,
  className,
}: {
  labels: AgentPromptLabels
  /** Where the button is, for analytics. */
  location: string
  className: string
}) => {
  const [isCopied, setIsCopied] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  useEffect(() => {
    if (!isCopied) return
    const timer = window.setTimeout(() => setIsCopied(false), 4000)
    return () => window.clearTimeout(timer)
  }, [isCopied])

  const copyPrompt = async () => {
    posthog.capture("agent_prompt_copied", { location })
    try {
      await navigator.clipboard.writeText(labels.agent_prompt)
      setIsCopied(true)
    } catch {
      setIsDialogOpen(true)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={copyPrompt}
        title={labels.agent_button_title}
        className={className}
      >
        {isCopied ? (
          <Check size={18} className="text-emerald-300" />
        ) : (
          <Bot
            size={18}
            className="text-purple-200 transition-transform duration-300 group-hover:scale-110"
          />
        )}
        <span aria-live="polite">
          {isCopied ? labels.agent_copied : labels.agent_button}
        </span>
      </button>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} size="lg">
        <Dialog.Panel
          centered
          className="bg-dark-8 border border-white/[0.12] w-[94%] max-w-2xl mx-auto rounded-2xl text-slate-100 shadow-2xl overflow-hidden"
        >
          <div className="flex items-center justify-between border-b border-white/[0.08] px-5 py-4">
            <Dialog.Title className="border-0 p-0 text-lg font-semibold text-white">
              {labels.agent_dialog_title}
            </Dialog.Title>
            <button
              type="button"
              onClick={() => setIsDialogOpen(false)}
              className="rounded-full border border-white/[0.12] p-1 text-slate-300 transition-colors hover:border-white/30 hover:text-white"
              aria-label={labels.agent_dialog_close_aria}
            >
              <X size={16} />
            </button>
          </div>
          <div className="p-4 sm:p-5">
            <textarea
              readOnly
              value={labels.agent_prompt}
              onFocus={event => event.currentTarget.select()}
              rows={4}
              className="w-full resize-none rounded-xl border border-white/[0.08] bg-black/40 p-4 text-sm leading-6 text-slate-200 outline-none focus:border-purple-300/50"
            />
          </div>
        </Dialog.Panel>
      </Dialog>
    </>
  )
}

export default CopyAgentPromptButton
